import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SignInButton, useAuth, useClerk } from "@clerk/clerk-react";
import { getAppConfig, watchLaunchModes } from "../../sim/env";
import { adminWhoami } from "../../sim/admin";
import { DEFAULT_PRELAUNCH_BANNER, DEFAULT_PRELAUNCH_HEADLINE, type PrelaunchConfig } from "../../sim/prelaunch";
import { authEnabled } from "../AuthGate";
import { BootSplash } from "../lazyPage";
import { Logo } from "../Logo";
import { accessStatus, claimAccess, redeemAccessCode, type AccessStatus } from "../../sim/access";
import { useEscapeClose } from "../useEscapeClose";
import { track } from "../../sim/analytics";

/**
 * THE PRE-LAUNCH GATE — wraps every route. While pre-launch mode is on (sim/prelaunch.ts; set on Admin → Pre-launch)
 * a visitor gets ONE page on every address — the landing page that points at the Kickstarter — and nothing of the
 * site or the game behind it. Admins see the site as normal, under a slim strip saying the mode is on.
 *
 * HOW AN ADMIN GETS IN: the tiny "Admin" link in the page's corner opens Clerk's sign-in. Once signed in the gate
 * asks the server who they are (/api/admin/me); an admin gets the site, anyone else stays on the page (with a quiet
 * "sign out"). The server refuses gameplay to non-admins as well, so the page is not the only lock.
 *
 * HOW A TESTER GETS IN: "Access Code Entry" in the other corner takes an early-access code an admin made for them
 * (server/accessCodes.js). Signed out, a good code gives this browser a pass (an HttpOnly cookie) that opens the site,
 * and they are sent to /play to create their account; the first time they are signed in the gate CLAIMS the pass,
 * binding the code to that account for good. Signed in, the code binds at once. Either way the server decides — the
 * gate only asks it (/api/access/status) — and the server is what lets them play.
 *
 * `?preview=prelaunch` shows the page to anyone — an admin's way to see what visitors see, on or off. In DEV a
 * key-less build has no sign-in, so there the server's own /api/admin/me answer decides (the smoke test mocks it),
 * and `?prelaunch=off` skips the gate entirely.
 */
export function PrelaunchGate({ children }: { children: ReactNode }) {
  const [cfg, setCfg] = useState<PrelaunchConfig | null>(null);
  const loc = useLocation();
  useEffect(() => {
    let alive = true;
    getAppConfig().then((c) => { if (alive) setCfg(c.prelaunch); });
    // FOLLOWED LIVE (sim/env.ts watchLaunchModes): a visitor waiting on the page when pre-launch goes off gets
    // the site within a minute — or as soon as they look at the tab again — without having to know to reload.
    const stop = watchLaunchModes((c) => { if (alive) setCfg(c.prelaunch); });
    return () => { alive = false; stop(); };
  }, []);
  const q = new URLSearchParams(loc.search);
  const preview = q.get("preview") === "prelaunch";
  if (!cfg) return <BootSplash />;
  if ((import.meta.env.DEV && q.get("prelaunch") === "off") || (!cfg.on && !preview)) return <>{children}</>;
  return authEnabled
    ? <SignedInGate cfg={cfg} preview={preview}>{children}</SignedInGate>
    : <KeylessGate cfg={cfg} preview={preview}>{children}</KeylessGate>;
}

/** What the server says about this visitor's early access; a failed read is "none" — the gate fails closed. */
const NO_ACCESS: AccessStatus = { prelaunch: true, granted: false, pass: false, signedIn: false };
async function readAccess(): Promise<AccessStatus> {
  try { return await accessStatus(); } catch { return NO_ACCESS; }
}

/**
 * Auth build: ask the server who this is — an admin, an account with early access, or a browser holding a pass. A
 * signed-in pass-holder's pass is CLAIMED here (bound to the account) before the site renders, so the game never
 * boots ahead of the access it needs.
 */
function SignedInGate({ cfg, preview, children }: { cfg: PrelaunchConfig; preview: boolean; children: ReactNode }) {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const [state, setState] = useState<{ admin: boolean; access: AccessStatus; claimError: string } | null>(null);
  const [recheck, setRecheck] = useState(0);
  useEffect(() => {
    if (!isLoaded) return;
    let alive = true;
    setState(null);
    void (async () => {
      const [who, access] = await Promise.all([
        isSignedIn ? adminWhoami().catch(() => ({ admin: false })) : Promise.resolve({ admin: false }),
        readAccess(),
      ]);
      let next = access, claimError = "";
      if (isSignedIn && !who.admin && !access.granted && access.pass) {
        try { await claimAccess(); next = { ...access, granted: true, pass: false }; }
        catch (e) { claimError = String((e as Error).message || e); next = { ...access, pass: false }; }
      }
      if (alive) setState({ admin: who.admin, access: next, claimError });
    })();
    return () => { alive = false; };
  }, [isLoaded, isSignedIn, userId, recheck]);
  if (!isLoaded || !state) return <BootSplash />;
  if (state.admin && !preview) return <><AdminStrip cfg={cfg} />{children}</>;
  if (!preview && (state.access.granted || state.access.pass)) {
    return <>{state.access.pass && !isSignedIn && <PassStrip />}{children}</>;
  }
  return (
    <PrelaunchPage cfg={cfg}
      corner={<AdminCorner signedIn={!!isSignedIn} admin={state.admin} />}
      access={<AccessEntry onAccepted={() => setRecheck((n) => n + 1)} notice={state.claimError} />} />
  );
}

/** No sign-in configured: in DEV the server's answer decides (so the page and the bypass can be previewed and
 *  tested); a real key-less server has no admins, so everyone gets the page. */
function KeylessGate({ cfg, preview, children }: { cfg: PrelaunchConfig; preview: boolean; children: ReactNode }) {
  const [admin, setAdmin] = useState<boolean | null>(import.meta.env.DEV ? null : false);
  const [access, setAccess] = useState<AccessStatus | null>(null);
  const [recheck, setRecheck] = useState(0);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    let alive = true;
    adminWhoami().then((w) => { if (alive) setAdmin(w.admin); }).catch(() => { if (alive) setAdmin(false); });
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    let alive = true;
    void readAccess().then((a) => { if (alive) setAccess(a); });
    return () => { alive = false; };
  }, [recheck]);
  if (admin === null || access === null) return <BootSplash />;
  if (admin && !preview) return <><AdminStrip cfg={cfg} />{children}</>;
  if (!preview && (access.granted || access.pass)) return <>{access.pass && <PassStrip />}{children}</>;
  return <PrelaunchPage cfg={cfg} corner={null} access={<AccessEntry onAccepted={() => setRecheck((n) => n + 1)} />} />;
}

/** Above the site for a visitor whose code opened it, until they have an account the code can be bound to. */
function PassStrip() {
  return (
    <div className="pl-strip pl-pass-strip" role="status" data-testid="prelaunch-pass-strip">
      🎟️ <b>Early access unlocked.</b> Create your account to start playing.
      <Link to="/play">Create an account and play</Link>
    </div>
  );
}

/**
 * "ACCESS CODE ENTRY" — the lower-left corner of the pre-launch page, and the small modal it opens. The code goes to
 * the server (POST /api/access/redeem); this component never decides anything itself. On success the gate re-asks the
 * server, and the visitor is sent to /play — the sign-up card, or the game if they were already signed in.
 */
function AccessEntry({ onAccepted, notice = "" }: { onAccepted: () => void; notice?: string }) {
  const [open, setOpen] = useState(!!notice);
  return (
    <>
      <button type="button" className="pl-access" data-testid="prelaunch-access-open" onClick={() => { track("cta_click", { id: "prelaunch.access" }); setOpen(true); }}>Access Code Entry</button>
      {open && <AccessModal notice={notice} onClose={() => setOpen(false)} onAccepted={onAccepted} />}
    </>
  );
}

function AccessModal({ notice, onClose, onAccepted }: { notice: string; onClose: () => void; onAccepted: () => void }) {
  const dialogRef = useEscapeClose(onClose);
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(notice);
  useEffect(() => { input.current?.focus(); }, []);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      await redeemAccessCode(code.trim());
      track("access_code", { ok: true });
      navigate("/play");
      onAccepted();
    } catch (ex) {
      track("access_code", { ok: false });
      setErr(String((ex as Error).message || ex));
      setBusy(false);
    }
  }
  return (
    <div className="pl-modal-overlay" onClick={onClose}>
      <form className="pl-modal" role="dialog" aria-modal="true" aria-label="Early access code" ref={dialogRef}
        onClick={(e) => e.stopPropagation()} onSubmit={(e) => void submit(e)} data-testid="prelaunch-access-modal">
        <h2 className="pl-modal-title">Early access</h2>
        <p className="pl-modal-sub">Got an early access code? Enter it to get into the game before launch.</p>
        <input ref={input} className="pl-modal-input" value={code} onChange={(e) => { setCode(e.target.value); setErr(""); }}
          placeholder="SG-XXXX-XXXX-XXXX" autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={40}
          aria-label="Access code" data-testid="prelaunch-access-input" />
        {err && <p className="pl-modal-err" role="alert">{err}</p>}
        <div className="pl-modal-actions">
          <button type="button" className="pl-modal-btn ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="pl-modal-btn" disabled={busy || !code.trim()} data-testid="prelaunch-access-submit">{busy ? "Checking…" : "Unlock"}</button>
        </div>
      </form>
    </div>
  );
}

/** What an admin sees while the mode is on. On the website: a strip above the page — a reminder, a preview link, the
 *  settings. IN THE GAME (/play): just "PRELAUNCH", floating over the top of the screen — it takes no space (the strip
 *  used to push the whole game interface down) and lets every click through to the game underneath. */
function AdminStrip({ cfg }: { cfg: PrelaunchConfig }) {
  const { pathname } = useLocation();
  if (pathname === "/play" || pathname.startsWith("/play/")) {
    return <div className="pl-ingame" role="status" aria-label="Pre-launch mode is on" data-testid="prelaunch-admin-strip">PRELAUNCH</div>;
  }
  return (
    <div className="pl-strip" role="status" data-testid="prelaunch-admin-strip">
      <b>Pre-launch mode is on.</b> Visitors only see the pre-launch page{cfg.kickstarterUrl ? "" : " (no Kickstarter link set yet)"}.
      <a href="/?preview=prelaunch">See it</a>
      <Link to="/admin/prelaunch">Settings</Link>
    </div>
  );
}

/** The corner of the page: a tiny Admin sign-in, or — signed in but not an admin — a quiet way out. */
function AdminCorner({ signedIn, admin }: { signedIn: boolean; admin: boolean }) {
  const clerk = useClerk();
  if (signedIn && !admin) {
    return (
      <span className="pl-corner">
        This account doesn&apos;t have early access · <button type="button" onClick={() => void clerk.signOut()}>Sign out</button>
      </span>
    );
  }
  if (signedIn) return <a className="pl-corner" href="/">Back to the site</a>;
  return <SignInButton mode="modal"><button type="button" className="pl-corner" data-testid="prelaunch-admin-login">Admin</button></SignInButton>;
}

/**
 * THE PRE-LAUNCH PAGE — deliberately almost empty: the logo, an unmistakable PRE-LAUNCH badge, one line saying the
 * game is coming to Kickstarter, and the banner, which (with the button under it) goes to the campaign page. No
 * navigation, no footer, no way into the site or the game. Theme-independent, like the shops' hero shots.
 */
export function PrelaunchPage({ cfg, corner, access = null }: { cfg: PrelaunchConfig; corner: ReactNode; access?: ReactNode }) {
  useEffect(() => {
    const was = document.title;
    document.title = "Shelf Garden: coming soon to Kickstarter";
    return () => { document.title = was; };
  }, []);
  const url = cfg.kickstarterUrl;
  const banner = <img className="pl-banner-img" src={cfg.bannerUrl || DEFAULT_PRELAUNCH_BANNER} alt="Shelf Garden: a cozy web game and a 9-piece enamel pin set" />;
  return (
    <main className="pl-page" data-testid="prelaunch-page">
      <div className="pl-glow" aria-hidden />
      <div className="pl-inner">
        <div className="pl-badge"><i aria-hidden /> Pre-launch</div>
        <Logo height={64} tone="light" />
        <h1 className="pl-headline">{cfg.headline || DEFAULT_PRELAUNCH_HEADLINE}</h1>
        {cfg.note && <p className="pl-note">{cfg.note}</p>}
        {url
          ? <a className="pl-banner" href={url} target="_blank" rel="noopener noreferrer" data-testid="prelaunch-banner" onClick={() => track("cta_click", { id: "prelaunch.kickstarter" })}>{banner}</a>
          : <div className="pl-banner nolink" data-testid="prelaunch-banner">{banner}</div>}
        {url
          ? <a className="pl-cta" href={url} target="_blank" rel="noopener noreferrer" onClick={() => track("cta_click", { id: "prelaunch.kickstarter" })}>Visit Shelf Garden on Kickstarter <span aria-hidden>→</span></a>
          : <div className="pl-cta-soon">The Kickstarter page is coming soon.</div>}
        <p className="pl-fine">This is a pre-launch page. The game opens to everyone when the campaign goes live.</p>
      </div>
      {access}
      {corner}
    </main>
  );
}
